/* global SeriesPromotionEnabled */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import {
  formatMetadata,
  formatRating,
  formatSeasonEpisode,
  formatSubRatings,
} from 'helpers/VideoHelper';
import Tile from 'components/TileLegacy';
import VideoModal from 'components/VideoModal';
import { SHOW_DETAIL_FIRST_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';
import { seriesVideoProcessor } from 'helpers/VideoProcessorHelper';
import { helperGetRelativeUrl, helperGetSeriesDetailUrl } from 'helpers/RouteHelper';
import { OVERLAY_VIDEO_MODAL } from 'helpers/FilterHelper';
import {
  getRequiresAuth,
  getSeasonEpisodeCounts,
  getPreviewVideoCTAText,
} from 'helpers/ContentHelper';
import { handleTealiumSeriesButtonAction } from '@foxcorp/delta-web-analytics';
import {
  getSeasonCount,
  TILE_TYPE_CONTINUE_WATCHING,
  TILE_TYPE_LATEST,
  TILE_TYPE_LIVE,
  TILE_TYPE_LATEST_PERSONALITY,
  TILE_TYPE_SERIES_PROMO,
} from 'screens/SeriesDetail/SeriesDetailHelpers';
import SeriesPromo from './SeriesPromo';
import Style from './FirstTile.module.scss';

const RESUME_TEXT = 'Resume';

class FirstTile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldRenderVideoModal: false,
      videoModalId: null,
    };
    this.toggleVideoModal = this.toggleVideoModal.bind(this);
  }

  toggleVideoModal({ id }) {
    const { shouldRenderVideoModal: prevShouldRender } = this.state;
    const { setOverlay } = this.props;
    if (prevShouldRender) {
      this.setState({
        shouldRenderVideoModal: false,
        videoModalId: null,
      });
      setOverlay(OVERLAY_VIDEO_MODAL, false);
    } else {
      this.setState(
        {
          shouldRenderVideoModal: true,
          videoModalId: id,
        },
        () => {
          setOverlay(OVERLAY_VIDEO_MODAL);
        },
      );
    }
  }

  renderVideoModal() {
    const { isAuthenticated, seriesData, tabs, videoModalProps } = this.props;
    const { shouldRenderVideoModal, videoModalId } = this.state;
    if (!shouldRenderVideoModal) return null;

    const oldestEpisode = _.get(seriesData, 'oldestEpisode');
    const name = _.get(seriesData, 'name');
    const seasonCount = getSeasonCount({ tabs });
    const episodeCount = _.get(seriesData, 'fullEpisodeCount', null);
    const showSubtitle = getSeasonEpisodeCounts({ seasonCount, episodeCount, useComma: true });
    const rating = formatRating(_.get(oldestEpisode, 'contentRating'));
    const subRating = formatSubRatings(_.get(oldestEpisode, 'subRatings'));
    const formattedRating = rating || subRating ? `${rating} ${subRating}` : null;
    const metadata = formatMetadata([formattedRating]);
    const description = _.get(seriesData, 'description');
    const showCode = _.get(seriesData, 'showCode');
    const seriesType = _.get(seriesData, 'seriesType');
    const titleLink = helperGetSeriesDetailUrl(showCode);

    const trailerPSU = _.get(oldestEpisode, 'playerScreenUrl');
    const watchLink = helperGetRelativeUrl({ url: trailerPSU });
    const episodeNumber = _.get(oldestEpisode, 'episodeNumber');
    const seasonNumber = _.get(oldestEpisode, 'seasonNumber');
    const seriesSeasonEpisode = formatSeasonEpisode(seasonNumber, episodeNumber);
    const requiresAuth = getRequiresAuth({ video: oldestEpisode });
    const lockButtons = requiresAuth && !isAuthenticated;

    const videoData = {
      name,
      showSubtitle,
      metadata,
      description,
      lockButtons,
      seriesSeasonEpisode,
      seriesType,
      showCode,
      titleLink,
      watchLink,
    };

    return (
      <VideoModal
        {...videoModalProps}
        data-test="first-tile-video-modal"
        toggleVideoModal={this.toggleVideoModal}
        videoData={videoData}
        videoId={videoModalId}
      />
    );
  }

  render() {
    const {
      account,
      breakpoint,
      displayUpsellCTA,
      isSingleEpisode,
      onClick,
      onLiveClick,
      seriesData,
      tabs,
      type,
      video,
    } = this.props;
    const {
      seriesDetail_latestEpisodeHeader: latestEpisodeHeaderText,
      global_startWatchingButton: startWatchingText,
      global_watchButton: watchText,
      global_watchLiveButton: watchLiveText,
      global_restartButton: restartText,
      global_watchNowButton: watchNowText,
    } = window.Language;

    if (!video) return null;

    const isLatestPersonality = type === TILE_TYPE_LATEST_PERSONALITY;
    const isLatest = type === TILE_TYPE_LATEST;
    const isLive = type === TILE_TYPE_LIVE;
    const isContinueWatching = type === TILE_TYPE_CONTINUE_WATCHING;
    const isSeriesPromo = type === TILE_TYPE_SERIES_PROMO;

    const seriesName = _.get(video, 'seriesName');
    const episodeName = _.get(video, 'name');
    const videoDescription = _.get(video, 'description');
    const seriesDescription = _.get(seriesData, 'description');
    const description = isSeriesPromo ? seriesDescription : videoDescription;
    const item = { ...video, displayUpsellCTA };
    const tileProps = seriesVideoProcessor({ item, isLive });
    const tileType = !isSingleEpisode ? 'tile-image-left' : null;
    const divider = !isSingleEpisode && <hr className={Style.hr} />;
    const containerStyle = isSingleEpisode ? Style.single : '';

    const seasonCount = getSeasonCount({ tabs });
    const episodeCount = _.get(seriesData, 'fullEpisodeCount', null);
    const seasonEpisodeHeaderText = getSeasonEpisodeCounts({ seasonCount, episodeCount });
    const displaySeasonAndEpisodeCounts = _.get(video, 'displaySeasonAndEpisodeCounts', true);
    const headerClasses = [Style.header];
    let headerText;
    if (isLatestPersonality) {
      const divider = seriesName && episodeName ? ' | ' : '';
      headerText = `${seriesName}${divider}${episodeName}`;
    } else if (isLive || isLatest) {
      headerText = latestEpisodeHeaderText;
    } else if (displaySeasonAndEpisodeCounts) {
      headerText = seasonEpisodeHeaderText;
      headerClasses.push(Style.uppercase);
    }
    const showHeader =
      headerText &&
      !isSingleEpisode &&
      (isLive || isLatest || isSeriesPromo || isLatestPersonality);
    const tileHeader = showHeader && <h2 className={classNames(headerClasses)}>{headerText}</h2>;

    const seasonNumber = _.get(video, 'seasonNumber');
    const episodeNumber = _.get(video, 'episodeNumber');
    let startWatchingButtonText;
    if (displaySeasonAndEpisodeCounts) {
      startWatchingButtonText =
        seasonNumber && episodeNumber
          ? `${startWatchingText} S${seasonNumber} E${episodeNumber}`
          : startWatchingText;
    } else {
      startWatchingButtonText = watchNowText;
    }
    const previewVideoPlayerScreenUrl = _.get(seriesData, 'previewVideo.member[0].playerScreenUrl');
    const previewVideoId = _.get(seriesData, 'previewVideo.member[0].id');
    const percentWatched = _.get(video, 'percentWatched', 0);
    const continueWatchingFromStart = isContinueWatching && percentWatched === 0;
    const actionButtons = [];

    const seriesPromoClickTracking = text => {
      if (!isSeriesPromo) return null;
      const trackClickProps = {
        subtitle: _.get(video, 'name'),
        collectionTitle: headerText,
        account,
        series: _.get(seriesData, 'name'),
      };
      return handleTealiumSeriesButtonAction({
        text,
        ...trackClickProps,
      });
    };

    if (SeriesPromotionEnabled) {
      if (isLive) {
        actionButtons.push({
          icon: 'restart-double',
          text: restartText,
          url: `${tileProps.link}mode/restart`,
        });
        actionButtons.push({
          icon: null,
          text: watchLiveText,
          url: tileProps.liveLink,
          onClick: () => {
            onLiveClick(video);
          },
        });
      } else if (continueWatchingFromStart || isLatest || isLatestPersonality) {
        actionButtons.push({
          icon: 'play-button',
          text: watchText,
          url: tileProps.link,
        });
      } else if (isContinueWatching) {
        actionButtons.push({
          icon: 'restart-double',
          text: restartText,
          url: `${tileProps.link}mode/restart`,
        });
        actionButtons.push({
          icon: null,
          text: RESUME_TEXT,
          url: tileProps.link,
        });
      } else if (isSeriesPromo) {
        actionButtons.push({
          icon: 'play-button',
          text: startWatchingButtonText,
          url: tileProps.link,
          onClick: () => seriesPromoClickTracking(startWatchingButtonText),
        });
        if (previewVideoPlayerScreenUrl) {
          actionButtons.push({
            icon: null,
            text: getPreviewVideoCTAText(seriesData),
            url: null,
            onClick: () => {
              this.toggleVideoModal({ id: previewVideoId });
            },
          });
        }
      }
    }

    const renderDescription = (
      <SeriesPromo
        ctaData={actionButtons}
        data-testid="first-tile-promo"
        description={description}
      />
    );

    const renderTile = (
      <Tile
        {...tileProps}
        tileType={tileType}
        theme={Style}
        onTileClick={onClick}
        onTitleClick={onClick}
        renderImage={image =>
          renderImage({
            breakpoint,
            imageType: SHOW_DETAIL_FIRST_THUMBNAIL_IMAGE,
            ...image,
          })
        }
        itemIndex={0}
        disableHoverStateActionButtons
        data-test="first-tile"
        ctaData={displayUpsellCTA ? [] : actionButtons}
        ctaPosition="end"
        firstTile
      />
    );

    return (
      <div className={containerStyle}>
        {tileHeader}
        {isSeriesPromo || isLatestPersonality ? renderDescription : renderTile}
        {divider}
        {this.renderVideoModal()}
      </div>
    );
  }
}

FirstTile.defaultProps = {
  account: null,
  breakpoint: null,
  displayUpsellCTA: false,
  isAuthenticated: false,
  isSingleEpisode: null,
  onClick: () => {},
  onLiveClick: () => {},
  seriesData: null,
  setOverlay: () => {},
  tabs: null,
  type: null,
  video: null,
  videoModalProps: {},
};

FirstTile.propTypes = {
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  displayUpsellCTA: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isSingleEpisode: PropTypes.bool,
  onClick: PropTypes.func,
  onLiveClick: PropTypes.func,
  seriesData: PropTypes.shape({}),
  setOverlay: PropTypes.func,
  tabs: PropTypes.arrayOf(PropTypes.shape({})),
  type: PropTypes.string,
  video: PropTypes.shape({}),
  videoModalProps: PropTypes.shape({}),
};

export default FirstTile;
